/** Every protected surface in the product. */
export const Resource = [
    'moulds',
    'catalog-masters', // the eleven v2 spec tables
    'locations',
    'stores',
    'checkpoints',
    'users',
    'scans',
    'transfers',
    'journeys',
    'nameplates',
    'reports',
    // Backups hold every record in the system, so reading the list is as
    // sensitive as reading the data itself - administrators only.
    'backups',
];
export const Action = ['read', 'create', 'update', 'delete', 'export'];
const ALL = Action;
const READ = ['read'];
const READ_EXPORT = ['read', 'export'];
const WRITE = ['read', 'create', 'update'];
/**
 * Omission means denial. A resource absent from a role's entry is forbidden for
 * every action - there is no implicit inheritance between roles, because
 * "supervisor is like a manager but…" is exactly how permission bugs are born.
 */
const MATRIX = {
    ADMIN: {
        moulds: ALL,
        'catalog-masters': ALL,
        locations: ALL,
        stores: ALL,
        checkpoints: ALL,
        users: ALL,
        scans: ['read', 'create', 'export'], // ledger is append-only for everyone
        transfers: ALL,
        journeys: ALL,
        nameplates: READ_EXPORT,
        reports: READ_EXPORT,
        backups: ['read', 'create', 'update'],
    },
    // Owns the catalog: the spec data and everything printed or engraved from it.
    INVENTORY_MANAGER: {
        moulds: ALL,
        'catalog-masters': ALL,
        locations: WRITE,
        stores: WRITE,
        checkpoints: WRITE,
        scans: ['read', 'create', 'export'],
        transfers: WRITE,
        journeys: WRITE,
        nameplates: READ_EXPORT,
        reports: READ_EXPORT,
    },
    // Works the floor: records where things are, never edits what they are.
    LOCATION_SUPERVISOR: {
        moulds: READ,
        'catalog-masters': READ,
        locations: READ,
        stores: READ,
        checkpoints: READ,
        scans: ['read', 'create'],
        transfers: READ,
        journeys: READ,
    },
    VIEWER: {
        moulds: READ,
        'catalog-masters': READ,
        locations: READ,
        stores: READ,
        checkpoints: READ,
        scans: READ,
        transfers: READ,
        journeys: READ,
        reports: READ_EXPORT,
    },
    // --- v1 legacy roles, retained until the client confirms retirement (decision D6) ---
    STORE_KEEPER: {
        moulds: READ,
        'catalog-masters': READ,
        locations: READ,
        stores: READ,
        checkpoints: READ,
        scans: ['read', 'create'],
        transfers: WRITE,
        journeys: WRITE,
    },
    GATE_SECURITY: {
        moulds: READ,
        locations: READ,
        checkpoints: READ,
        scans: ['read', 'create'], // further narrowed to assignedCheckpointIds server-side
        transfers: READ,
        journeys: READ,
    },
};
/** Single source of truth for "may this role do this?". */
export function can(role, action, resource) {
    if (!role)
        return false;
    return MATRIX[role]?.[resource]?.includes(action) ?? false;
}
/** Resources a role may read - the sync engine's replication filter. */
export function readableResources(role) {
    return Resource.filter((r) => can(role, 'read', r));
}
/** True when the role may change anything at all - drives "read-only" UI chrome. */
export function isReadOnly(role) {
    return !Resource.some((r) => can(role, 'create', r) || can(role, 'update', r) || can(role, 'delete', r));
}
export const ROLE_LABELS = {
    ADMIN: 'Administrator',
    INVENTORY_MANAGER: 'Inventory Manager',
    LOCATION_SUPERVISOR: 'Location Supervisor',
    VIEWER: 'Viewer',
    STORE_KEEPER: 'Store Keeper',
    GATE_SECURITY: 'Gate Security',
};
export const ROLE_DESCRIPTIONS = {
    ADMIN: 'Full access, including users and deletions.',
    INVENTORY_MANAGER: 'Owns the mould catalog, master data and nameplate exports.',
    LOCATION_SUPERVISOR: 'Scans moulds to record their location. Cannot edit catalog data.',
    VIEWER: 'Read-only access to the catalog and reports.',
    STORE_KEEPER: 'Legacy v1 role - scanning and transfers.',
    GATE_SECURITY: 'Legacy v1 role - scanning at assigned gate checkpoints only.',
};
/**
 * Roles whose scanning is narrowed to named checkpoints.
 *
 * Gate security is the reason the field exists: someone posted on the dispatch
 * gate should not be able to record a mould as having passed the vendor gate on
 * the other side of the site. Location supervisors are included for the same
 * reason - a supervisor for Mira Road has no business recording movements at
 * Vasai.
 *
 * Named here rather than in either app because both have to agree. The People
 * page offering a checkpoint tick-list that the server then ignores is worse
 * than offering nothing: it tells an administrator they have restricted someone
 * when they have not.
 */
export const RESTRICTED_BY_CHECKPOINT = new Set([
    'GATE_SECURITY',
    'LOCATION_SUPERVISOR',
]);
/**
 * May this person scan at this checkpoint?
 *
 * An empty assignment means unrestricted, which is right for an administrator
 * and a trap for a gate guard - so the People page says so in words rather than
 * leaving an empty tick-list to be read either way.
 */
export function mayScanAt(role, assignedCheckpointIds, checkpointId) {
    if (!can(role, 'create', 'scans'))
        return false;
    if (!role || !RESTRICTED_BY_CHECKPOINT.has(role))
        return true;
    if (assignedCheckpointIds.length === 0)
        return true;
    return assignedCheckpointIds.includes(checkpointId);
}
//# sourceMappingURL=rbac.js.map